import React from "react";
import { useAppSelector as useSelector } from "../../../store";
import { TDayData } from "../../../store/candidate/types";
import Checkbox from "../Inputs/Checkbox";
import DayCell from "./DayCell";
import { dayNames } from "./MyCalendar";

interface WeekRowProps {
  weekName: string;
  weekData: TDayData[];
  handleCellChange: (weekKey: string, day: string, checked: boolean) => void;
  handleBulkChangeRow: (weekKey: string, checked: boolean) => void;
  onRefresh: () => void;
  setSelectedDaysResetTrigger?: React.Dispatch<React.SetStateAction<boolean>>;
  checkedRows: string[];
}

const WeekRow: React.FC<WeekRowProps> = ({
  weekName,
  weekData,
  handleCellChange,
  handleBulkChangeRow,
  onRefresh,
  setSelectedDaysResetTrigger,
  checkedRows,
}) => {
  const { candidateCalendarData } = useSelector((state) => state.candidateJobs);
  /**
   *
   * @returns The week number extracted from the weekName. Ex "week_1" returns 1.
   */
  const getWeekNumber = (): number => {
    const match = weekName.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  /**
   * Checks if all days in the week are checked.
   * If any one day is unchecked, it returns false.
   * @function isRowChecked
   * @param {string} weekName - The name of the week.
   * @returns boolean - Returns true if all days in the week are checked, false otherwise.
   */
  const isRowChecked = (): boolean => {
    const week = candidateCalendarData[weekName];
    if (!week) return false;

    return dayNames.every((day) => week[day]?.checked === true);
  };

  const isRowDisabled = (weekKey: string): boolean => {
    const weekData: TDayData[] = candidateCalendarData[weekKey];
    if (!weekData) return false;
    // Check if any week has accepted_count > 0 for the given day
    return Object.values(weekData).some(
      (dayData: TDayData) => dayData?.accepted_count > 0
    );
  };

  return (
    <tr>
      <td
        className={`align-middle ${
          getWeekNumber() === 1 ? "radius-top-left-8px" : ""
        }`}
      >
        <div className="d-flex flex-column align-items-center gap-10">
          <div className="fs-16 fw-700 text-black">W{getWeekNumber()}</div>
          <div className="theme-check">
            <Checkbox
              className="theme-check-input"
              id={`week-${getWeekNumber()}`}
              checked={checkedRows?.includes(`week_${getWeekNumber()}`)}
              onChange={(checked) => {
                setSelectedDaysResetTrigger &&
                  setSelectedDaysResetTrigger(true);
                handleBulkChangeRow(weekName, checked);
              }}
              label={""}
              themeCheck={true}
              // disabled={isRowChecked() && isRowDisabled(weekName)}
            />
          </div>
        </div>
      </td>
      {weekData &&
        Object.entries(weekData)
          .filter(([dayKey]) => dayNames.includes(dayKey)) // Only show weekdays (Mon-Fri)
          .map(([dayKey, dayData]: [string, TDayData], index: number) => (
            <DayCell
              key={index}
              weekName={weekName}
              dayKey={dayKey}
              dayData={dayData}
              handleCellChange={handleCellChange}
              onRefresh={onRefresh}
            />
          ))}
    </tr>
  );
};

export default WeekRow;
